import { useState } from 'react';
import { TablePagination } from '../../components/TablePagination';
import { Group, Table, Button, Badge, Modal, TextInput, Textarea, Switch, Stack, ActionIcon, Divider, Text, Select, Alert, SegmentedControl } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconPencil, IconBuildingCommunity, IconTrash, IconAlertTriangle } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { EmptyState } from '../../components/EmptyState';
import { TableToolbar } from '../../components/TableToolbar';
import { TableSkeleton } from '../../components/TableSkeleton';
import { FetchOverlay } from '../../components/FetchOverlay';
import { usePagedQuery } from '../../hooks/usePagedQuery';
import { TableExport } from '../../components/TableExport';
import { formatDate } from '../../lib/format';
import { slugError, tenantBaseDomain, contractNumberPrefix, LEGACY_REQUISITES_SLUG } from '../../lib/tenant';
import { TENANT_ADMIN_PASSWORD_MIN, LOGIN_MIN } from '../../lib/password';
import { StrongPasswordInput, TenantAdminsSection } from '../../components/TenantAdminsSection';

const errMsg = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

interface Tenant {
  id: string; name: string; slug: string; isActive: boolean; note?: string | null; createdAt: string;
  _count: { users: number; students: number };
  qurilmaSoni?: number; kutilmoqdaSoni?: number;
}

/** Platforma superadmini — maktablar (tenantlar) boshqaruvi. */
export default function Tenants() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [createOpen, ch] = useDisclosure(false);
  const [editing, setEditing] = useState<Tenant | null>(null);
  const [delConfirm, setDelConfirm] = useState('');

  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [active, setActive] = useState('');
  const [sort, setSort] = useState('createdAt:desc');

  /* Filtrlar BITTA joyda: jadval ham, eksport ham AYNI obyektni oladi. */
  const filtrlar = {
    q: qDeb || undefined,
    active: active || undefined,
    sort,
  };
  const list = usePagedQuery<Tenant>('sa-tenants', '/superadmin/tenants', filtrlar, 20);
  // Filtrlangan bo'sh natija ma'lumot umuman yo'qligidan farqli matn ko'rsatadi
  const filterActive = !!qDeb || !!active;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['sa-tenants'] });

  const createForm = useForm({
    initialValues: { name: '', slug: '', adminLogin: '', adminPassword: '', adminFullName: '', adminGender: '', note: '' },
    validate: {
      name: (v) => (!v.trim() ? t('tenantsPage.nameRequired') : null),
      // Slug qoidasi tahrirlash bilan BITTA funksiyada (lib/tenant.ts → slugError)
      slug: (v) => { const k = slugError(v); return k ? t(`tenantsPage.${k}`) : null; },
      adminLogin: (v) => (v.trim().length < LOGIN_MIN ? t('tenantsPage.loginMin') : null),
      adminPassword: (v) => (v.length < TENANT_ADMIN_PASSWORD_MIN ? t('tenantsPage.passwordMin', { n: TENANT_ADMIN_PASSWORD_MIN }) : null),
      adminGender: (v) => (!v ? t('tenantAdmins.genderRequired') : null),
    },
  });
  const createMut = useMutation({
    mutationFn: (v: typeof createForm.values) => api.post('/superadmin/tenants', v),
    onSuccess: () => { notifications.show({ color: 'green', message: t('tenantsPage.created') }); invalidate(); createForm.reset(); ch.close(); },
    onError: (e: unknown) => notifications.show({ color: 'red', message: errMsg(e, t('tenantsPage.error')) }),
  });

  const editForm = useForm({
    initialValues: { name: '', isActive: true, note: '', slug: '' },
    validate: {
      name: (v) => (!v.trim() ? t('tenantsPage.nameRequired') : null),
      slug: (v) => { const k = slugError(v); return k ? t(`tenantsPage.${k}`) : null; },
    },
  });
  const editMut = useMutation({
    mutationFn: async (v: typeof editForm.values) =>
      (await api.patch<{ requisitesSaved?: boolean }>(`/superadmin/tenants/${editing!.id}`, v)).data,
    onSuccess: (data) => {
      notifications.show({ color: 'green', message: t('tenantsPage.saved') });
      // SATA eski slugidan ketdi — amaldagi zaxira rekvizitlar maktab yozuviga saqlandi
      if (data?.requisitesSaved) notifications.show({ color: 'blue', message: t('tenantsPage.requisitesSaved'), autoClose: 8000 });
      invalidate();
      setEditing(null);
    },
    onError: (e: unknown) => notifications.show({ color: 'red', message: errMsg(e, t('tenantsPage.error')) }),
  });
  const openEdit = (tn: Tenant) => {
    setEditing(tn);
    setDelConfirm('');
    editForm.setValues({ name: tn.name, isActive: tn.isActive, note: tn.note ?? '', slug: tn.slug });
    editForm.clearErrors();
  };

  /** Manzil ko'rinishi: `<slug>.<domen>` (subdomen rejimi o'chiq bo'lsa — umumiy "domen"). */
  const hostOf = (slug: string) => `${slug}.${tenantBaseDomain() || t('tenantsPage.domainFallback')}`;
  const slugChanged = !!editing && editForm.values.slug !== editing.slug;

  /** Subdomen o'zgarsa — aniq tasdiq (eski manzil ishlamay qoladi). */
  const submitEdit = (v: typeof editForm.values) => {
    if (!editing || v.slug === editing.slug) return editMut.mutate(v);
    modals.openConfirmModal({
      title: t('tenantsPage.slugConfirmTitle'),
      centered: true,
      children: (
        <Text size="sm">
          {t('tenantsPage.slugConfirmMsg', { old: hostOf(editing.slug), new: hostOf(v.slug) })}
        </Text>
      ),
      labels: { confirm: t('tenantsPage.slugConfirmOk'), cancel: t('tenantsPage.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => editMut.mutate(v),
    });
  };

  const deleteMut = useMutation({
    mutationFn: (tn: Tenant) => api.delete(`/superadmin/tenants/${tn.id}`, { params: { confirm: tn.slug } }),
    onSuccess: (_d, tn) => { notifications.show({ color: 'green', message: t('tenantsPage.deleted', { name: tn.name }) }); invalidate(); setEditing(null); setDelConfirm(''); },
    onError: (e: unknown) => notifications.show({ color: 'red', message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('tenantsPage.error') }),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => api.patch(`/superadmin/tenants/${id}`, { isActive }),
    onSuccess: invalidate,
  });

  return (
    <>
      <PageHeader title={t('tenantsPage.title')} subtitle={t('tenantsPage.totalCount', { n: list.total })}
        action={<Button leftSection={<IconPlus size={16} />} onClick={ch.open}>{t('tenantsPage.add')}</Button>} />

      <TableToolbar
        search={search} onSearch={setSearch} searchPlaceholder={t('tenantsPage.searchPlaceholder')}
        sort={sort} onSort={setSort}
        sortOptions={[
          { value: 'createdAt:desc', label: t('tenantsPage.sortNewest') },
          { value: 'createdAt:asc', label: t('tenantsPage.sortOldest') },
          { value: 'name:asc', label: t('tenantsPage.sortNameAsc') },
          { value: 'name:desc', label: t('tenantsPage.sortNameDesc') },
        ]}
      >
        <Select
          placeholder={t('tenantsPage.statusFilter')}
          data={[{ value: 'true', label: t('tenantsPage.active') }, { value: 'false', label: t('tenantsPage.inactive') }]}
          value={active || null}
          onChange={(v) => setActive(v ?? '')}
          clearable w={160}
        />
        <TableExport<Tenant>
          endpoint="/superadmin/tenants"
          params={filtrlar}
          filename={t('nav.tenants')}
          ustunlar={[
            { key: 'name', label: t('qc.className') },
            { key: 'slug', label: 'slug' },
            { key: 'users', label: t('nav.staff'), get: (r) => r._count.users },
            { key: 'students', label: t('nav.students'), get: (r) => r._count.students },
            { key: 'isActive', label: t('fields.status'), get: (r) => (r.isActive ? '+' : '-') },
          ]}
        />
      </TableToolbar>

      <SectionCard title={t('common.list')} icon={IconBuildingCommunity} p={0} pos="relative">
        {list.isLoading ? (
          <TableSkeleton rows={6} cols={6} />
        ) : !list.items.length ? (
          <EmptyState
            icon={IconBuildingCommunity}
            title={filterActive ? t('common.noMatch') : t('tenantsPage.emptyTitle')}
            description={filterActive ? undefined : t('tenantsPage.emptyDesc')}
          />
        ) : (
          <Table.ScrollContainer minWidth={720}>
            <FetchOverlay visible={list.fetching} />
            <Table highlightOnHover verticalSpacing="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('tenantsPage.colName')}</Table.Th>
                  <Table.Th>{t('tenantsPage.colSlug')}</Table.Th>
                  <Table.Th>{t('tenantsPage.colCounts')}</Table.Th>
                  <Table.Th>{t('tenantsPage.colQurilmalar')}</Table.Th>
                  <Table.Th>{t('tenantsPage.colCreated')}</Table.Th>
                  <Table.Th w={90}>{t('tenantsPage.colActive')}</Table.Th>
                  <Table.Th w={50}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.items.map((tn) => (
                  <Table.Tr key={tn.id}>
                    <Table.Td fw={600}>{tn.name}</Table.Td>
                    <Table.Td><Badge variant="light" color="grape" ff="monospace">{tn.slug}</Badge></Table.Td>
                    <Table.Td>{tn._count.users} / {tn._count.students}</Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm">{tn.qurilmaSoni ?? 0}</Text>
                        {(tn.kutilmoqdaSoni ?? 0) > 0 && <Badge size="sm" color="yellow" variant="light">+{tn.kutilmoqdaSoni}</Badge>}
                      </Group>
                    </Table.Td>
                    <Table.Td>{formatDate(tn.createdAt)}</Table.Td>
                    <Table.Td><Switch checked={tn.isActive} onChange={(e) => toggleMut.mutate({ id: tn.id, isActive: e.currentTarget.checked })} /></Table.Td>
                    <Table.Td><ActionIcon variant="subtle" onClick={() => openEdit(tn)}><IconPencil size={16} /></ActionIcon></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination
        page={list.page}
        pages={list.pages}
        total={list.total}
        limit={list.limit}
        onPage={list.setPage}
        onLimit={list.setLimit}
      />

      {/* Yaratish */}
      <Modal opened={createOpen} onClose={ch.close} title={t('tenantsPage.createTitle')} size="md">
        <form onSubmit={createForm.onSubmit((v) => createMut.mutate(v))}>
          <Stack>
            <TextInput label={t('tenantsPage.name')} required {...createForm.getInputProps('name')} />
            <TextInput label={t('tenantsPage.slug')} placeholder={t('tenantsPage.slugPlaceholder')} required description={t('tenantsPage.slugDesc')} {...createForm.getInputProps('slug')} />
            <Textarea label={t('tenantsPage.note')} autosize minRows={1} {...createForm.getInputProps('note')} />
            <Divider label={t('tenantsPage.adminSection')} labelPosition="center" />
            <TextInput label={t('tenantsPage.adminLogin')} required autoComplete="off" {...createForm.getInputProps('adminLogin')} />
            <StrongPasswordInput
              label={t('tenantsPage.adminPassword')}
              required
              description={t('tenantAdmins.passwordDesc', { n: TENANT_ADMIN_PASSWORD_MIN })}
              value={createForm.values.adminPassword}
              onChange={(v) => createForm.setFieldValue('adminPassword', v)}
              error={createForm.errors.adminPassword}
            />
            <TextInput label={t('tenantsPage.adminFullName')} {...createForm.getInputProps('adminFullName')} />
            <Stack gap={4}>
              <Text size="sm" fw={500}>{t('tenantAdmins.gender')} <Text span c="red">*</Text></Text>
              <SegmentedControl fullWidth
                data={[
                  { value: 'MALE', label: t('tenantAdmins.male') },
                  { value: 'FEMALE', label: t('tenantAdmins.female') },
                ]}
                value={createForm.values.adminGender}
                onChange={(v) => createForm.setFieldValue('adminGender', v)} />
              {createForm.errors.adminGender && <Text size="xs" c="red">{createForm.errors.adminGender}</Text>}
            </Stack>
            <Group justify="flex-end">
              <Button variant="default" onClick={ch.close}>{t('tenantsPage.cancel')}</Button>
              <Button type="submit" loading={createMut.isPending}>{t('tenantsPage.create')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Tahrirlash */}
      <Modal opened={!!editing} onClose={() => setEditing(null)} title={editing?.name ?? t('tenantsPage.editFallbackTitle')} size="lg">
        <form onSubmit={editForm.onSubmit(submitEdit)} noValidate>
          <Stack>
            <TextInput label={t('tenantsPage.name')} required {...editForm.getInputProps('name')} />
            <TextInput
              label={t('tenantsPage.slug')}
              required
              description={t('tenantsPage.slugDesc')}
              autoComplete="off"
              styles={{ input: { fontFamily: 'monospace' } }}
              {...editForm.getInputProps('slug')}
              onChange={(e) => editForm.setFieldValue('slug', e.currentTarget.value.trim().toLowerCase())}
            />
            {slugChanged && editing && (
              <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />} title={t('tenantsPage.slugChangeTitle')}>
                <Stack gap={4}>
                  <Text size="sm">{t('tenantsPage.slugChangeOld', { old: hostOf(editing.slug), new: hostOf(editForm.values.slug || '…') })}</Text>
                  <Text size="sm">
                    {t('tenantsPage.slugChangeContracts', {
                      old: contractNumberPrefix(editing.slug),
                      new: contractNumberPrefix(editForm.values.slug),
                    })}
                  </Text>
                  {editing.slug === LEGACY_REQUISITES_SLUG && (
                    <Text size="sm">{t('tenantsPage.slugChangeLegacy')}</Text>
                  )}
                </Stack>
              </Alert>
            )}
            <Textarea label={t('tenantsPage.note')} autosize minRows={1} {...editForm.getInputProps('note')} />
            <Switch label={t('tenantsPage.activeLabel')} description={t('tenantsPage.activeDesc')} checked={editForm.values.isActive} onChange={(e) => editForm.setFieldValue('isActive', e.currentTarget.checked)} />
            <Group justify="flex-end"><Button type="submit" loading={editMut.isPending}>{t('tenantsPage.save')}</Button></Group>
          </Stack>
        </form>

        {/* Maktab adminlari — parolni qayta o'rnatish / loginni almashtirish */}
        <Divider my="lg" label={t('tenantAdmins.section')} labelPosition="center" />
        {editing && <TenantAdminsSection tenantId={editing.id} />}

        {/* Xavfli zona — maktabni butunlay o'chirish */}
        <Divider my="lg" label={t('tenantsPage.dangerZone')} labelPosition="center" color="red" />
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            {t('tenantsPage.deleteWarning', { users: editing?._count.users ?? 0, students: editing?._count.students ?? 0 })}
            {' '}{t('tenantsPage.deleteConfirmHint')} <Text span fw={700} ff="monospace">{editing?.slug}</Text>
          </Text>
          <TextInput placeholder={editing?.slug} value={delConfirm} onChange={(e) => setDelConfirm(e.currentTarget.value)} />
          <Button color="red" variant="light" leftSection={<IconTrash size={16} />}
            disabled={!editing || delConfirm !== editing.slug} loading={deleteMut.isPending}
            onClick={() => editing && deleteMut.mutate(editing)}>
            {t('tenantsPage.deleteBtn')}
          </Button>
        </Stack>
      </Modal>
    </>
  );
}
